<template>
    <div class="assistent-functionality-form">
        <flex-container>
            <flex-container
                :gap="null"
                direction="column"
            >
                <text-heading>Functionaliteiten</text-heading>
                <text-description :color="'gray-800'">
                    {{ functionalityDescription }}
                </text-description>
            </flex-container>

            <!-- Alert block for requesting a new function-->
            <flex-container>
                <alert-block :color="'primary'" fill>
                    <template #default>
                        <flex-container
                            justify-content="space-between"
                            align-items="center"
                            no-wrap
                        >
                            <flex-container size="auto">
                                Staat jouw gewenste functionaliteit er niet tussen?<br>
                                Laat het ons dan weten, dan helpen we je graag.
                            </flex-container>
                            <flex-container
                                size="auto"
                                justify-content="flex-end"
                            >
                                <button-text @click="modal?.openOverlay()">
                                    <text-decorator bold>
                                        Verzoek
                                    </text-decorator>
                                </button-text>
                            </flex-container>
                            <request-new-functionality-modal
                                ref="modal"
                                :assistent="assistent"
                                functionality="Functie"
                            >
                                <template #input>
                                    <text-area-input
                                        height="200px"
                                        placeholder="Beschrijf de functie en welke informatie verzameld moet worden."
                                    />
                                </template>
                            </request-new-functionality-modal>
                        </flex-container>
                    </template>
                </alert-block>
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Verwijder 'Powered by Winck'
                        <chip-informative :color="'primary'">
                            Additioneel
                        </chip-informative> <chip-informative :color="'primary'">
                            €20,- p/m
                        </chip-informative>
                    </text-heading>
                    <text-description>
                        Dit verwijdert de verwijzing naar Winck onderaan het chat scherm.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.withoutBranding"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Gegevens vragen
                        <chip-informative :color="'tertiary'">
                            Additioneel
                        </chip-informative> <chip-informative :color="'tertiary'">
                            €5,- p/m
                        </chip-informative>
                    </text-heading>
                    <text-description>
                        De AI Agent vraagt aan iedereen aan het begin van de chat verplicht naar contact gegevens.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.requestContactDataEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <form-validation>
                <flex-container
                    v-if="assistent.features.requestContactDataEnabled"
                    :gap="null"
                >
                    <input-group v-model="assistent.features.requiredContactDataFields" :schema="array().of(object()).min(1)">
                        <template #input>
                            <flex-container>
                                <checkbox-group-input
                                    :columns="1"
                                    :items="ContactDataField.values"
                                    label-key-name="label"
                                    value-key-name="value"
                                />
                            </flex-container>
                        </template>
                    </input-group>
                </flex-container>
            </form-validation>

            <flex-container>
                <alert-block :color="'orange-500'">
                    <template #prefix>
                        <svg-icon :icon="WarningIcon" />
                    </template>
                    <template #default>
                        Deze functie verlaagt mogelijk het aantal leads
                    </template>
                </alert-block>
            </flex-container>

            <flex-container>
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Waarde indicatie
                        <chip-informative :color="'primary'">
                            Additioneel
                        </chip-informative> <chip-informative :color="'primary'">
                            €{{ homeValuationPrice }},- p/m
                        </chip-informative>
                    </text-heading>
                    <text-description>
                        De AI Agent kan automatisch de waarde van een woning bepalen volgens officiële NRVT richtlijnen, wanneer dit nuttig is voor het gesprek met de website bezoeker.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.appraisalEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <card-expandable with-content with-collapse-arrow>
                    Verplichte additionele vragen
                    <template v-if="assistent.features.appraisalRequiredFields.length > 0">
                        ({{ assistent.features.appraisalRequiredFields.length }})
                    </template>
                    <template #content>
                        <flex-container :gap="Size.sm">
                            <flex-container>
                                <flex-container :size="4">
                                    <text-heading sub>
                                        Veld
                                    </text-heading>
                                </flex-container>
                                <flex-container :size="6">
                                    <flex-container justify-content="space-between">
                                        <text-heading sub>
                                            Verplicht
                                        </text-heading>
                                        <text-heading sub>
                                            Niet van toepassing
                                        </text-heading>
                                    </flex-container>
                                </flex-container>
                            </flex-container>

                            <horizontal-line no-margin />

                            <template v-for="field in FeatureFieldName.values">
                                <input-group
                                    v-if="appraisalFieldValues[field.value]"
                                    :key="field.value"
                                    v-model="appraisalFieldValues[field.value]"
                                    style="width: 100%;"
                                    inline
                                >
                                    <template #input>
                                        <flex-container align-items="flex-end">
                                            <flex-container :size="4">
                                                <text-decorator>
                                                    {{ field.label }}
                                                </text-decorator>
                                            </flex-container>
                                            <flex-container :size="6">
                                                <radio-group-input
                                                    :items="[
                                                        { value: 'required' },
                                                        { value: 'no' },
                                                    ]"
                                                    :columns="2"
                                                    justify-columns="center"
                                                    @input="updateAppraisalRequiredFields"
                                                />
                                            </flex-container>
                                        </flex-container>
                                    </template>
                                </input-group>
                            </template>
                        </flex-container>
                    </template>
                </card-expandable>
            </flex-container>

            <flex-container
                :gap="Size.md"
            >
                <transition name="fade">
                    <flex-container
                        v-if="assistent.features.appraisalEnabled"
                        direction="column"
                        :padding="Size.md"
                        :color="'gray-100'"
                        no-wrap
                    >
                        <input-group
                            v-model="assistent.features.appraisalEstimateOffsetType"
                            :schema="EstimateOffsetType.validationSchema.required()"
                        >
                            <template #label>
                                <input-label>
                                    Schattingsindicatie:
                                </input-label>
                            </template>
                            <template #input>
                                <select-input :items="EstimateOffsetType.values" placeholder="Kies een schattingsindicatie" />
                            </template>
                        </input-group>

                        <text-description v-if="EstimateOffsetType.PESSIMISTIC.equals(assistent.features.appraisalEstimateOffsetType)">
                            De AI Agent zal iets onder de daadwerkelijk ingeschatte waarde zitten.
                        </text-description>
                        <text-description v-if="EstimateOffsetType.CONSERVATIVE.equals(assistent.features.appraisalEstimateOffsetType)">
                            De AI Agent zal de exact ingeschatte waarde geven.
                        </text-description>
                        <text-description v-if="EstimateOffsetType.OPTIMISTIC.equals(assistent.features.appraisalEstimateOffsetType)">
                            De AI Agent zal iets boven de daadwerkelijk ingeschatte waarde zitten.
                        </text-description>
                    </flex-container>
                </transition>

                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        WOZ-waarde
                        <chip-informative :color="'primary'">
                            Additioneel
                        </chip-informative> <chip-informative :color="'primary'">
                            €{{ wozPrice }},- p/m
                        </chip-informative>
                    </text-heading>
                    <text-description>
                        De AI Agent kan WOZ-waardes van de afgelopen jaren opvragen voor een woning, wanneer dit relevant is voor het gesprek.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.wozEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Energielabel
                        <chip-informative :color="'primary'">
                            Additioneel
                        </chip-informative> <chip-informative :color="'primary'">
                            €{{ energyLabelPrice }},- p/m
                        </chip-informative>
                    </text-heading>
                    <text-description>
                        De AI Agent kan het huidige of voorlopige energielabel van een woning opvragen, wanneer dit van belang is voor het gesprek.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.energyLabelEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Funda-check
                        <chip-informative :color="'primary'">
                            Additioneel
                        </chip-informative> <chip-informative :color="'primary'">
                            €{{ fundaCheckPrice }},- p/m
                        </chip-informative>
                    </text-heading>
                    <text-description>
                        De AI Agent kan de Funda-pagina van een woning bekijken en controleren, en geeft hierbij voorrang aan de vraagprijs op Funda indien er een woningwaarde indicatie wordt opgevraagd.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.fundaEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Omgevingsinformatie
                        <chip-informative :color="'primary'">
                            Additioneel
                        </chip-informative> <chip-informative :color="'primary'">
                            €{{ surroundingPrice }},- p/m
                        </chip-informative>
                    </text-heading>
                    <text-description>
                        De AI Agent kan informatie opvragen over de buurt, zoals scholen, winkels, en openbaar vervoer, en dit gebruiken in het gesprek als het relevant is.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.areaInfoEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Netto maandlasten hypotheek
                        <chip-informative :color="'primary'">
                            Additioneel
                        </chip-informative>
                        <chip-informative :color="'primary'">
                            €{{ hypotheekPrice }},- p/m
                        </chip-informative>
                    </text-heading>
                    <text-description>
                        Het is waardevol om te weten wat een woning netto per maand kost.
                        Schakel deze functie in, en de AI Agent berekent dit direct tijdens het gesprek.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.hypotheekMaandlastenEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <card-expandable with-content with-collapse-arrow>
                    Verplichte additionele vragen
                    <template v-if="assistent.features.hypotheekMaandlastenRequiredFields.length > 0">
                        ({{ assistent.features.hypotheekMaandlastenRequiredFields.length }})
                    </template>
                    <template #content>
                        <flex-container :gap="Size.sm">
                            <flex-container>
                                <flex-container :size="4">
                                    <text-heading sub>
                                        Veld
                                    </text-heading>
                                </flex-container>
                                <flex-container :size="6">
                                    <flex-container justify-content="space-between">
                                        <text-heading sub>
                                            Verplicht
                                        </text-heading>
                                        <text-heading sub>
                                            Niet van toepassing
                                        </text-heading>
                                    </flex-container>
                                </flex-container>
                            </flex-container>

                            <horizontal-line no-margin />

                            <template v-for="field in FeatureFieldName.values">
                                <input-group
                                    v-if="maandlastenFieldValues[field.value]"
                                    :key="field.value"
                                    v-model="maandlastenFieldValues[field.value]"
                                    style="width: 100%;"
                                    inline
                                >
                                    <template #input>
                                        <flex-container align-items="flex-end">
                                            <flex-container :size="4">
                                                <text-decorator>
                                                    {{ field.label }}
                                                </text-decorator>
                                            </flex-container>
                                            <flex-container :size="6">
                                                <radio-group-input
                                                    :items="[
                                                        { value: 'required' },
                                                        { value: 'no' },
                                                    ]"
                                                    :columns="2"
                                                    justify-columns="center"
                                                    @input="updateMaandlastenRequiredFields"
                                                />
                                            </flex-container>
                                        </flex-container>
                                    </template>
                                </input-group>
                            </template>
                        </flex-container>
                    </template>
                </card-expandable>
            </flex-container>

            <flex-container>
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Maximale hypotheek berekening
                        <chip-informative :color="'primary'">
                            Additioneel
                        </chip-informative>
                        <chip-informative :color="'primary'">
                            €{{ maxMortgagePrice }},- p/m
                        </chip-informative>
                    </text-heading>
                    <text-description>
                        Gebruik deze functie wanneer een gebruiker wil weten hoeveel hij kan lenen voor een huis
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.maximumMortgageEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <card-expandable with-content with-collapse-arrow>
                    Verplichte additionele vragen
                    <template v-if="assistent.features.maximumMortgageRequiredFields.length > 0">
                        ({{ assistent.features.maximumMortgageRequiredFields.length }})
                    </template>
                    <template #content>
                        <flex-container :gap="Size.sm">
                            <flex-container>
                                <flex-container :size="4">
                                    <text-heading sub>
                                        Veld
                                    </text-heading>
                                </flex-container>
                                <flex-container :size="6">
                                    <flex-container justify-content="space-between">
                                        <text-heading sub>
                                            Verplicht
                                        </text-heading>
                                        <text-heading sub>
                                            Niet van toepassing
                                        </text-heading>
                                    </flex-container>
                                </flex-container>
                            </flex-container>

                            <horizontal-line no-margin />

                            <template v-for="field in FeatureFieldName.values">
                                <input-group
                                    v-if="maximumMortgageFieldValues[field.value]"
                                    :key="field.value"
                                    v-model="maximumMortgageFieldValues[field.value]"
                                    style="width: 100%;"
                                    inline
                                >
                                    <template #input>
                                        <flex-container align-items="flex-end">
                                            <flex-container :size="4">
                                                <text-decorator>
                                                    {{ field.label }}
                                                </text-decorator>
                                            </flex-container>
                                            <flex-container :size="6">
                                                <radio-group-input
                                                    :items="[
                                                        { value: 'required' },
                                                        { value: 'no' },
                                                    ]"
                                                    :columns="2"
                                                    justify-columns="center"
                                                    @input="updateMortgageRequiredFields"
                                                />
                                            </flex-container>
                                        </flex-container>
                                    </template>
                                </input-group>
                            </template>
                        </flex-container>
                    </template>
                </card-expandable>
            </flex-container>

            <flex-container>
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Postcode-check
                    </text-heading>
                    <text-description>
                        De AI Agent kan controleren of de ingevulde postcode en huisnummer correct zijn, zodat de juiste informatie wordt gegeven.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.postalCodeEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Google Streetview
                    </text-heading>
                    <text-description>
                        De AI Agent kan een foto van de locatie van een woning tonen, als dit relevant is voor het gesprek.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.streetviewEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        E-mails versturen
                    </text-heading>
                    <text-description>
                        De AI Agent kan zelfstandig e-mails sturen naar website bezoekers, bijvoorbeeld met een samenvatting van het gesprek de e-mails bevatten altijd een disclaimer dat ze door een AI zijn verstuurd.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.emailEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container
                :gap="null"
            >
                <transition name="fade">
                    <flex-container
                        v-if="assistent.features.emailEnabled && assistent.emailRouting !== null"
                        :padding="Size.md"
                        :color="'gray-100'"
                        no-wrap
                    >
                        <input-group
                            v-model="assistent.emailRouting.fromName"
                            :schema="string().min(5).required()"
                        >
                            <template #label>
                                <input-label>
                                    Afzendernaam:
                                </input-label>
                            </template>
                            <template #input>
                                <text-input placeholder="Vul een afzendernaam in" />
                            </template>
                        </input-group>
                        <input-group
                            v-model="assistent.emailRouting.forwardEmailAddress"
                            :schema="string().min(5).email().required()"
                        >
                            <template #label>
                                <input-label>
                                    Antwoorden doorsturen naar:
                                </input-label>
                            </template>
                            <template #input>
                                <text-input placeholder="Vul een e-mailadres in" />
                            </template>
                        </input-group>
                    </flex-container>
                </transition>

                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                v-if="false"
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        SMS versturen
                    </text-heading>
                    <text-description>
                        De AI Agent kan zelfstandig smsjes sturen naar websitebezoekers, bijvoorbeeld met een samenvatting van het gesprek. De smsjes bevatten altijd een disclaimer dat ze door een AI zijn verstuurd.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.smsEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container v-if="false">
                <horizontal-line no-margin />
            </flex-container>

            <flex-container
                size="full"
                justify-content="space-between"
                align-items="center"
                no-wrap
            >
                <div>
                    <text-heading sub>
                        Dichtstbijzijnde vestiging
                    </text-heading>
                    <text-description>
                        De AI Agent kan berekenen welke vestiging het dichtst bij de locatie van de bezoeker of het te verkopen object ligt.
                    </text-description>
                </div>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.features.nearestOfficeEnabled"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>
        </flex-container>
    </div>
</template>

<script setup lang="ts">
    import {
        type ExposedOverlay,
        FlexContainer,
        HorizontalLine,
        InputGroup,
        Size,
        SwitchInput,
        TextAreaInput,
        TextDescription,
        TextHeading,
    } from '@keyvue/one-ui';
    import { computed, onMounted, type Ref, ref, watch } from 'vue';
    import {
        type Assistent,
        ContactDataField,
        EmailRouting,
        EmailRoutingId,
        EstimateOffsetType,
        FeatureFieldName,
    } from '@keyvue/one-api-client';
    import { array, boolean, object, string } from 'yup';
    import WarningIcon from '@keyvue/one-material-icons/icons/warning.svg?component';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';
    import RequestNewFunctionalityModal from '@/ai/assistent/components/forms/RequestNewFunctionalityModal.vue';

    const { hasWinckBranding } = useAppBranding();

    const props = defineProps<{ modelValue: Assistent }>();
    const emit = defineEmits<{
        (e: 'update:modelValue', value: Assistent): void;
    }>();

    const assistent = ref(props.modelValue) as Ref<Assistent>;
    const modal = ref<ExposedOverlay>();

    const functionalityDescription = computed(() => {
        if (hasWinckBranding()) {
            return 'De AI Agent kan veel verschillende dingen voor jouw bezoekers doen, zoals het berekenen van woningwaardes, opvragen van buurtinformatie, en het versturen van e-mails. Maar jij hebt de controle! Op deze pagina kun je zelf beslissen welke functionaliteiten de agent wel of niet mag gebruiken. Dit geeft je de mogelijkheid om de agent precies zo in te stellen dat hij aansluit bij jouw wensen en behoeften.';
        }
        return 'De AI Agent biedt diverse mogelijkheden om jouw bezoekers te ondersteunen, maar jij bepaalt wat hij wel of niet mag doen. Op deze pagina kun je eenvoudig instellen welke functies de agent mag gebruiken, zodat hij perfect aansluit bij jouw wensen en behoeften.';
    });

    const homeValuationPrice = computed(() => { return hasWinckBranding() ? 30 : 90; });
    const wozPrice = computed(() => { return hasWinckBranding() ? 10 : 50; });
    const energyLabelPrice = computed(() => { return hasWinckBranding() ? 5 : 55; });
    const fundaCheckPrice = computed(() => { return hasWinckBranding() ? 15 : 55; });
    const surroundingPrice = computed(() => { return hasWinckBranding() ? 10 : 70; });
    const hypotheekPrice = computed(() => { return hasWinckBranding() ? 10 : 30; });
    const maxMortgagePrice = computed(() => { return hasWinckBranding() ? 10 : 30; });

    const appraisalFieldValues = ref<{ [key: string]: { value: string } }>({});
    const maandlastenFieldValues = ref<{ [key: string]: { value: string } }>({});
    const maximumMortgageFieldValues = ref<{ [key: string]: { value: string } }>({});

    function createEmailRouting() {
        assistent.value.emailRouting = new EmailRouting(
            EmailRoutingId.generate(),
            '',
            '',
            new Date(),
            new Date(),
        );
    }

    async function load() {
        const { features } = assistent.value;

        function mapFieldValues(requiredFields: FeatureFieldName[]) {
            return Object.fromEntries(
                FeatureFieldName.values.map((featureFieldName) => [
                    featureFieldName.value,
                    {
                        value: requiredFields.some((field) => field.value === featureFieldName.value) ? 'required' : 'no',
                    },
                ]),
            );
        }

        appraisalFieldValues.value = mapFieldValues(features.appraisalRequiredFields);
        maandlastenFieldValues.value = mapFieldValues(features.hypotheekMaandlastenRequiredFields);
        maximumMortgageFieldValues.value = mapFieldValues(features.maximumMortgageRequiredFields);
    }

    function updateAppraisalRequiredFields() {
        assistent.value.features.appraisalRequiredFields = FeatureFieldName.values.filter(
            (featureFieldName) => {
                return appraisalFieldValues.value[featureFieldName.value]?.value === 'required';
            },
        );
    }

    function updateMaandlastenRequiredFields() {
        assistent.value.features.hypotheekMaandlastenRequiredFields = FeatureFieldName.values.filter(
            (featureFieldName) => {
                return maandlastenFieldValues.value[featureFieldName.value]?.value === 'required';
            },
        );
    }

    function updateMortgageRequiredFields() {
        assistent.value.features.maximumMortgageRequiredFields = FeatureFieldName.values.filter(
            (contactDataRequirements) => {
                return maximumMortgageFieldValues.value[contactDataRequirements.value]?.value === 'required';
            },
        );
    }

    watch(props.modelValue, (value) => {
        assistent.value = value;
    });

    watch(assistent, (value: Assistent) => {
        if (value.features.emailEnabled && value.emailRouting === null) {
            createEmailRouting();
        }

        emit('update:modelValue', value);
    }, { deep: true });

    onMounted(() => {
        load();
    });
</script>

<style scoped lang="scss">
.assistent-functionality-form {
    box-sizing: border-box;
    height: 100%;
    overflow-y: scroll;
    padding-bottom: size(md);
    position: relative;
}
</style>
